import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Save, Plus, X } from "lucide-react";

const API = '/api';

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newDomain, setNewDomain] = useState("");
  const [newMappingDomain, setNewMappingDomain] = useState("");
  const [newMappingBrand, setNewMappingBrand] = useState("");

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/dashboard/settings`),
      axios.get(`${API}/brands/`),
    ]).then(([sRes, bRes]) => {
      setSettings(sRes.data);
      setBrands(bRes.data.brands);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    await axios.put(`${API}/dashboard/settings`, {
      tenant_name: settings.tenant_name,
      internal_domains: settings.internal_domains,
      default_brand_id: settings.default_brand_id,
      domain_brands: settings.domain_brands || {},
    });
    setSaving(false);
  };

  const addDomainMapping = () => {
    if (!newMappingDomain.trim() || !newMappingBrand) return;
    setSettings({
      ...settings,
      domain_brands: {
        ...(settings.domain_brands || {}),
        [newMappingDomain.trim().toLowerCase()]: newMappingBrand,
      },
    });
    setNewMappingDomain("");
    setNewMappingBrand("");
  };

  const removeDomainMapping = (domain) => {
    const updated = { ...(settings.domain_brands || {}) };
    delete updated[domain];
    setSettings({ ...settings, domain_brands: updated });
  };

  const addDomain = () => {
    if (!newDomain.trim()) return;
    setSettings({
      ...settings,
      internal_domains: [...(settings.internal_domains || []), newDomain.trim().toLowerCase()],
    });
    setNewDomain("");
  };

  const removeDomain = (domain) => {
    setSettings({
      ...settings,
      internal_domains: (settings.internal_domains || []).filter((d) => d !== domain),
    });
  };

  if (loading) return <div className="p-8 text-sm text-[#5E5E5E]">Loading settings...</div>;
  if (!settings) return <div className="p-8 text-sm text-[#A4262C]">Failed to load settings.</div>;

  return (
    <div className="p-8 max-w-3xl" data-testid="settings-page">
      <h1 className="font-heading text-2xl font-semibold tracking-tight text-[#171717]">Organization Settings</h1>
      <p className="text-sm text-[#5E5E5E] mt-1">Tenant configuration for signature processing</p>

      <div className="mt-6 bg-white border border-[#E1DFDD] rounded-sm">
        <div className="p-5 space-y-5">
          <div>
            <label className="text-xs font-semibold tracking-[0.05em] uppercase text-[#5E5E5E] mb-1.5 block">
              Tenant Name
            </label>
            <Input
              value={settings.tenant_name || ""}
              onChange={(e) => setSettings({ ...settings, tenant_name: e.target.value })}
              className="max-w-md border-[#E1DFDD] rounded-sm"
              data-testid="tenant-name-input"
            />
          </div>

          <div>
            <label className="text-xs font-semibold tracking-[0.05em] uppercase text-[#5E5E5E] mb-1.5 block">
              Default Brand
            </label>
            <select
              value={settings.default_brand_id || ""}
              onChange={(e) => setSettings({ ...settings, default_brand_id: e.target.value })}
              className="max-w-md w-full border border-[#E1DFDD] rounded-sm px-3 py-2 text-sm text-[#171717] focus:outline-none focus:border-[#0F6CBD]"
              data-testid="default-brand-select"
            >
              <option value="">-- None --</option>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>{b.display_name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold tracking-[0.05em] uppercase text-[#5E5E5E] mb-1.5 block">
              Internal Domains
            </label>
            <p className="text-xs text-[#5E5E5E] mb-2">
              Emails between these domains won't get a signature (internal routing).
            </p>
            <div className="flex flex-wrap gap-2 mb-3">
              {(settings.internal_domains || []).map((domain) => (
                <span
                  key={domain}
                  className="inline-flex items-center gap-1 bg-[#EBF3FC] text-[#0F6CBD] px-2.5 py-1 rounded-sm text-xs font-mono"
                >
                  {domain}
                  <button onClick={() => removeDomain(domain)} className="hover:text-[#A4262C]" data-testid={`remove-domain-${domain}`}>
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2 max-w-md">
              <Input
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                placeholder="example.com"
                className="border-[#E1DFDD] rounded-sm"
                data-testid="new-domain-input"
                onKeyDown={(e) => e.key === "Enter" && addDomain()}
              />
              <Button
                variant="outline"
                onClick={addDomain}
                className="border-[#E1DFDD] rounded-sm shrink-0"
                data-testid="add-domain-btn"
              >
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold tracking-[0.05em] uppercase text-[#5E5E5E] mb-1.5 block">
              Domain &rarr; Brand Mapping
            </label>
            <p className="text-xs text-[#5E5E5E] mb-2">
              Route senders to a brand (and its templates) based on their email domain.
            </p>
            <div className="space-y-2 mb-3">
              {Object.entries(settings.domain_brands || {}).map(([domain, brandId]) => {
                const brandName = brands.find((b) => b.id === brandId)?.display_name || brandId;
                return (
                  <div key={domain} className="flex items-center gap-2 bg-[#F3F2F1] px-3 py-2 rounded-sm">
                    <span className="font-mono text-xs text-[#0F6CBD] flex-1">@{domain}</span>
                    <span className="text-xs text-[#5E5E5E]">&rarr;</span>
                    <span className="text-xs font-medium text-[#171717] flex-1">{brandName}</span>
                    <button onClick={() => removeDomainMapping(domain)} className="text-[#A4262C] hover:text-[#A4262C]/80" data-testid={`remove-mapping-${domain}`}>
                      <X size={12} />
                    </button>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2 max-w-lg">
              <Input
                value={newMappingDomain}
                onChange={(e) => setNewMappingDomain(e.target.value)}
                placeholder="domain.com"
                className="border-[#E1DFDD] rounded-sm flex-1"
                data-testid="new-mapping-domain-input"
              />
              <select
                value={newMappingBrand}
                onChange={(e) => setNewMappingBrand(e.target.value)}
                className="flex-1 border border-[#E1DFDD] rounded-sm px-3 py-2 text-sm text-[#171717]"
                data-testid="new-mapping-brand-select"
              >
                <option value="">-- Brand --</option>
                {brands.map((b) => (
                  <option key={b.id} value={b.id}>{b.display_name}</option>
                ))}
              </select>
              <Button variant="outline" onClick={addDomainMapping} className="border-[#E1DFDD] rounded-sm shrink-0" data-testid="add-mapping-btn">
                <Plus size={14} className="mr-1" /> Map
              </Button>
            </div>
          </div>

          <div className="pt-2 border-t border-[#E1DFDD]">
            <div className="flex items-center gap-3">
              <p className="text-xs text-[#5E5E5E]">Azure Tenant ID:</p>
              <code className="text-xs font-mono text-[#5E5E5E] bg-[#F3F2F1] px-2 py-0.5 rounded-sm">
                {settings.azure_tenant_id || "N/A"}
              </code>
              <span className="text-[10px] bg-[#107C10] text-white px-1.5 py-0.5 rounded-sm">
                {settings.graph_connected ? "Connected" : "Not connected"}
              </span>
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-[#E1DFDD] bg-[#FAFAFA]">
          <Button
            onClick={save}
            disabled={saving}
            className="bg-[#0F6CBD] text-white hover:bg-[#005A9E] rounded-sm"
            data-testid="save-settings-btn"
          >
            <Save size={14} className="mr-2" />
            {saving ? "Saving..." : "Save Settings"}
          </Button>
        </div>
      </div>
    </div>
  );
}
